import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDeepLink } from '../../../hooks/useDeepLink';
import { isAppleDevice } from '../../../components/Donee/components/common/utils';
import { appLinks } from '../../../utils/webConfig';
import { copyToClipboardAsync } from '../../../utils/clipboard';
import { useDonorProfile } from '../../../api';

export const useEventPage = () => {
    const [searchParams] = useSearchParams();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [showCopiedModal, setShowCopiedModal] = useState<boolean>(false);
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const getParamCI = useCallback(
        (name: string): string | null => {
            const lower = name.toLowerCase();
            let match: string | null = null;
            searchParams.forEach((value, key) => {
                if (match === null && key.toLowerCase() === lower) {
                    match = value;
                }
            });
            return match;
        },
        [searchParams],
    );
    const eventId = getParamCI('eventId');
    const ticketId = getParamCI('ticketId');
    const { donorProfile, isLoading: donorLoading } = useDonorProfile();
    const { getDeepLink } = useDeepLink(undefined, undefined, () => undefined);

    const deepLinkPath = useMemo(() => {
        const params = new URLSearchParams();
        if (eventId) params.set('eventId', eventId);
        if (ticketId) params.set('ticketId', ticketId);
        const qs = params.toString();
        return qs ? `/dl/event/${eventId}` : '/dl/tickets';
    }, [eventId, ticketId]);

    const resolveEventDeepLink = useCallback(async () => {
        const s = await getDeepLink(
            undefined,
            donorProfile,
            deepLinkPath,
            undefined,
        );
        const link = s || `${window.location.origin}${deepLinkPath}`;
        setUrl(link);
        return link;
    }, [deepLinkPath, getDeepLink, donorProfile]);

    const handleGetApp = useCallback(() => {
        if (isCopying) return;
        setIsCopying(true);
        const linkPromise = resolveEventDeepLink();
        if (isAppleDevice()) {
            return linkPromise
                .then(() => setShowCopiedModal(true))
                .finally(() => setIsCopying(false));
        }
        return copyToClipboardAsync(linkPromise)
            .then(() => {
                window.location.href = appLinks.events.android;
            })
            .finally(() => setIsCopying(false));
    }, [resolveEventDeepLink, isCopying]);

    const handleDownload = useCallback(() => {
        window.location.href = appLinks.events.apple;
    }, []);

    const handleCloseCopiedModal = useCallback(() => {
        setShowCopiedModal(false);
    }, []);

    return {
        metaTags: {
            title: 'Event',
            description: 'Download the app to view event',
        },
        eventId,
        donorLoading,
        url,
        showCopiedModal,
        isCopying,
        qrCodeValue: window.location.href,
        handleGetApp,
        handleDownload,
        handleCloseCopiedModal,
    };
};
